import { AlertTriangle, Inbox, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../cn';
import { Button } from './button';

type StateProps = { title: string; children?: ReactNode; className?: string };

export function EmptyState({ title, children, className }: StateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-dashed border-line-200 p-8 text-center',
        className,
      )}
    >
      <Inbox className="size-6 text-muted-600" aria-hidden />
      <p className="font-medium text-navy-900">{title}</p>
      {children ? <div className="text-sm text-muted-600">{children}</div> : null}
    </div>
  );
}

export function ErrorState({
  title,
  children,
  onRetry,
  className,
}: StateProps & { onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-danger-700/40 bg-white p-8 text-center',
        className,
      )}
    >
      <AlertTriangle className="size-6 text-danger-700" aria-hidden />
      <p className="font-medium text-danger-700">{title}</p>
      {children ? <div className="text-sm text-ink-900">{children}</div> : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-2">
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function PermissionDeniedState({ title, children, className }: StateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-line-200 bg-white p-8 text-center',
        className,
      )}
    >
      <Lock className="size-6 text-navy-800" aria-hidden />
      <p className="font-medium text-navy-900">{title}</p>
      {children ? <div className="text-sm text-muted-600">{children}</div> : null}
    </div>
  );
}

export function LoadingState({ label, className }: { label: string; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={cn('flex flex-col gap-3', className)}>
      <span className="sr-only">{label}</span>
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-16 animate-pulse rounded-lg bg-line-200/60" aria-hidden />
      ))}
    </div>
  );
}
